import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  Eye,
  Check,
  Home,
  Image as ImageIcon,
  FileText,
  ShoppingBag,
  Monitor,
  Upload,
  Grid,
  Star,
  Video,
  Layers,
  Heart,
  Mail,
  Plus,
  Trash2,
  Quote,
  Users,
  BookOpen,
  PlayCircle,
  Settings,
  Type,
  Edit,
  Info,
  X,
  CheckCircle,
  AlertCircle,
  Sparkles
} from 'lucide-react';
import axios from 'axios';
import EnhancedColorPicker from '../EnhancedColorPicker.jsx';
import EnhancedFontSelector from '../EnhancedFontSelector.jsx';
import Base from '../Base';

// Template Configuration - defines which sections each template needs (matches JSON content keys)
const TEMPLATE_SECTIONS = {
  1: ['brand', 'business'],
  2: ['brand', 'categories', 'business'],
  3: ['brand', 'hero', 'categories', 'business'],
  4: ['brand', 'hero', 'categories', 'fashion_trends', 'videos', 'business'],
  5: ['brand', 'hero', 'categories', 'fashion_trends', 'videos', 'business'],
  6: ['brand', 'business'],
  7: ['brand', 'hero', 'business'],
  8: ['brand', 'hero', 'about', 'categories', 'business'],
  9: ['brand', 'hero', 'categories', 'lookbook', 'business'],
  10: ['brand', 'hero', 'categories', 'business'],
  11: ['brand', 'hero', 'collections', 'categories', 'statement', 'business'],
  12: ['brand', 'hero', 'collections', 'statement', 'categories', 'business'],
  13: ['brand', 'hero', 'categories', 'workshop', 'business'],
  14: ['brand', 'hero', 'gallery', 'featured', 'statement', 'instagram', 'business'],
  15: ['brand', 'hero', 'categories', 'brand_story', 'video', 'business'],
  16: ['brand', 'hero', 'luxury_statement', 'prestige_services', 'business'],
  17: ['brand', 'hero', 'categories', 'craftsmanship', 'human_connection', 'business'],
  18: ['brand', 'hero', 'categories', 'brand_statement', 'business'],
  19: ['brand', 'hero', 'featured_stories', 'quote', 'business'],
  20: ['brand', 'hero', 'portfolio', 'artist_statement', 'collections', 'business']
};

// Section metadata (matches JSON content keys only)
const SECTION_METADATA = {
  brand: { label: 'Brand Information', icon: ShoppingBag },
  hero: { label: 'Hero Section', icon: Monitor },
  about: { label: 'About / Vision', icon: Info },
  categories: { label: 'Categories', icon: Grid },
  collections: { label: 'Collections', icon: Layers },
  fashion_trends: { label: 'Fashion Trends', icon: Star },
  videos: { label: 'Videos', icon: Video },
  lookbook: { label: 'Lookbook', icon: BookOpen },
  gallery: { label: 'Gallery', icon: ImageIcon },
  featured: { label: 'Featured Products', icon: Star },
  instagram: { label: 'Instagram Feed', icon: ImageIcon },
  statement: { label: 'Statement', icon: Quote },
  workshop: { label: 'Workshop', icon: Settings },
  brand_story: { label: 'Brand Story', icon: BookOpen },
  video: { label: 'Video', icon: PlayCircle },
  luxury_statement: { label: 'Luxury Statement', icon: Quote },
  prestige_services: { label: 'Prestige Services', icon: Star },
  craftsmanship: { label: 'Craftsmanship', icon: Settings },
  human_connection: { label: 'Human Connection', icon: Heart },
  brand_statement: { label: 'Brand Statement', icon: Quote },
  featured_stories: { label: 'Featured Stories', icon: FileText },
  quote: { label: 'Quote', icon: Quote },
  portfolio: { label: 'Portfolio', icon: ImageIcon },
  artist_statement: { label: 'Artist Statement', icon: Quote },
  business: { label: 'Business Information', icon: FileText }
};

const MainPageForm = () => {
  const { templateId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [activeSection, setActiveSection] = useState('brand');

  // File upload states
  const [uploadedFiles, setUploadedFiles] = useState({});
  const [previewImages, setPreviewImages] = useState({});

  // Theme state - stores original theme from the template JSON
  const [themeData, setThemeData] = useState(null);

  // Modal states
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const [formData, setFormData] = useState({
    brand: { name: '', logo: '', tagline: '', description: '' },
    hero: {
      title: '',
      subtitle: '',
      description: '',
      background_image: '',
      backgroundImage: '',
      cta_text: 'Shop Collection',
      cta_link: '#products',
      ctaText: 'Shop Collection',
      ctaLink: '#products',
      values: [{ label: '' }]
    },
    about: {
      title: '',
      description: ''
    },
    categories: [
      { name: '', title: '', count: '', description: '', image: '' }
    ],
    collections: [
      { title: '', name: '', count: '', description: '', image: '' }
    ],
    fashion_trends: [
      { name: '', text: '', image: '' }
    ],
    videos: [
      { url: '', title: '', description: '', thumbnail: '' }
    ],
    lookbook: [
      { title: '', subtitle: '', image: '' }
    ],
    gallery: [
      { title: '', image: '' }
    ],
    featured: [
      { title: '', image: '', price: '' }
    ],
    instagram: [
      { image: '' }
    ],
    statement: {
      title: '',
      text: '',
      background_image: '',
      backgroundImage: ''
    },
    workshop: {
      title: '',
      description: '',
      background_image: '',
      values: [
        { title: '', description: '' }
      ]
    },
    brand_story: {
      title: '',
      description: '',
      stats: [
        { value: '', label: '' }
      ]
    },
    video: {
      thumbnail: '',
      url: ''
    },
    luxury_statement: {
      title: '',
      description: ''
    },
    prestige_services: [
      { title: '', description: '' }
    ],
    craftsmanship: {
      title: '',
      description: '',
      stats: [
        { value: '', label: '' }
      ]
    },
    human_connection: {
      title: '',
      quote: '',
      author: ''
    },
    brand_statement: {
      title: '',
      description: '',
      values: [
        { title: '', description: '' }
      ]
    },
    featured_stories: [
      { title: '', excerpt: '', category: '', image: '' }
    ],
    quote: {
      text: '',
      author: ''
    },
    portfolio: [
      { title: '', image: '' }
    ],
    artist_statement: {
      title: '',
      text: ''
    },
    business: {
      contact: {
        business_name: '',
        email: '',
        phone: '',
        address: '',
        working_hours: 'Mon-Sat: 10AM-8PM'
      }
    }
  });

  // Get sections for current template
  const templateSections = TEMPLATE_SECTIONS[parseInt(templateId)] || ['brand', 'hero', 'business'];
  const sections = templateSections.map(sectionId => ({
    id: sectionId,
    ...SECTION_METADATA[sectionId]
  }));

  useEffect(() => {
    loadPageData();
  }, [templateId]);

  useEffect(() => {
    // Set first available section as active
    if (sections.length > 0) {
      setActiveSection(sections[0].id);
    }
  }, [templateId]);

  const loadPageData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`/api/template/${templateId}/page/main`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      console.log('Loaded page data response:', response.data);

      // Check if there's saved data
      if (response.data.success && response.data.data) {
        const savedData = response.data.data;

        // Extract theme data if available and set it in formData.theme
        if (savedData.theme) {
          console.log('Loading theme data:', savedData.theme);
          setThemeData(savedData.theme);

          // Convert theme to formData format for color pickers
          const themeForForm = {
            colors: {
              primary: Array.isArray(savedData.theme.colors?.primary)
                ? savedData.theme.colors.primary[0]
                : savedData.theme.colors?.primary || '#2C1810',
              secondary: savedData.theme.colors?.secondary || '#ffffff',
              accent: savedData.theme.colors?.accent || '#C9A882',
              background: savedData.theme.colors?.background || '#ffffff',
              text: savedData.theme.colors?.text?.primary || savedData.theme.colors?.text || '#111827',
              surface: savedData.theme.colors?.surface || '#ffffff',
              border: savedData.theme.colors?.border || '#E5E7EB'
            },
            typography: {
              heading: savedData.theme.typography?.heading || 'sans-serif',
              body: savedData.theme.typography?.body || 'sans-serif'
            }
          };

          setFormData(prev => ({
            ...prev,
            theme: themeForForm
          }));
        }

        // If it's v2.0 format JSON, extract the content
        if (savedData.version === '2.0' && savedData.content) {
          console.log('Loading v2.0 format data');
          setFormData(prev => normalizeFormData({ ...prev, ...savedData.content }));
        }
        // If it's old format or direct content
        else if (savedData.content) {
          console.log('Loading content data');
          setFormData(prev => normalizeFormData({ ...prev, ...savedData.content }));
        }
        // If the whole saved data is the form data
        else {
          console.log('Loading saved data directly');
          setFormData(prev => normalizeFormData({ ...prev, ...savedData }));
        }
      }
      // Fallback to old response format
      else if (response.data.content) {
        console.log('Loading old format content');
        setFormData(prev => normalizeFormData({ ...prev, ...response.data.content }));
        // Also check for theme in old format
        if (response.data.theme) {
          setThemeData(response.data.theme);
          // Convert theme to formData format
          const themeForForm = {
            colors: {
              primary: Array.isArray(response.data.theme.colors?.primary)
                ? response.data.theme.colors.primary[0]
                : response.data.theme.colors?.primary || '#2C1810',
              secondary: response.data.theme.colors?.secondary || '#ffffff',
              accent: response.data.theme.colors?.accent || '#C9A882',
              background: response.data.theme.colors?.background || '#ffffff',
              text: response.data.theme.colors?.text?.primary || response.data.theme.colors?.text || '#111827'
            },
            typography: {
              heading: response.data.theme.typography?.heading || 'sans-serif',
              body: response.data.theme.typography?.body || 'sans-serif'
            }
          };
          setFormData(prev => ({ ...prev, theme: themeForForm }));
        }
      }
    } catch (error) {
      console.error('Error loading main page data:', error);
      // Don't show error for 404 - it just means no data saved yet
      if (error.response && error.response.status !== 404) {
        console.error('Unexpected error:', error.response);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (isDraft = true) => {
    try {
      setSaving(true);

      // Get authentication token
      const token = localStorage.getItem('access_token');
      if (!token) {
        alert('Please login to save changes');
        navigate('/login');
        return;
      }

      // Check if there are any uploaded files
      const hasFiles = Object.keys(uploadedFiles).length > 0;

      // Normalize field names before saving (ensures both camelCase and snake_case variants exist)
      const normalizedData = normalizeFormData(formData);

      if (hasFiles) {
        // Use FormData for multipart/form-data when files are present
        const formDataToSend = new FormData();
        formDataToSend.append('status', isDraft ? 'draft' : 'complete');
        formDataToSend.append('content', JSON.stringify(normalizedData));

        // Append all uploaded files
        Object.entries(uploadedFiles).forEach(([fieldName, file]) => {
          formDataToSend.append(fieldName, file);
        });

        await axios.post(`/api/template/${templateId}/page/main/save`, formDataToSend, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });
      } else {
        // Use JSON when no files
        await axios.post(`/api/template/${templateId}/page/main/save`, {
          status: isDraft ? 'draft' : 'complete',
          content: normalizedData
        }, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
      }

      setLastSaved(new Date());

      // Show success modal
      setShowSuccessModal(true);
    } catch (error) {
      console.error('Error saving:', error);

      // Extract detailed error message
      let errMsg = 'Error saving changes. Please try again.';
      if (error.response) {
        console.error('Error response data:', error.response.data);
        console.error('Error response status:', error.response.status);

        if (error.response.data) {
          if (typeof error.response.data === 'string') {
            errMsg = error.response.data;
          } else if (error.response.data.error) {
            errMsg = error.response.data.error;
          } else if (error.response.data.message) {
            errMsg = error.response.data.message;
          } else if (error.response.data.detail) {
            errMsg = error.response.data.detail;
          }
        }
      } else if (error.request) {
        errMsg = 'No response from server. Please check your connection.';
      } else {
        errMsg = error.message || errMsg;
      }

      setErrorMessage(errMsg);
      setShowErrorModal(true);
    } finally {
      setSaving(false);
    }
  };

  // Handle modal close and navigation
  const handleSuccessModalClose = () => {
    setShowSuccessModal(false);
    navigate(`/template-builder/${templateId}`);
  };

  const handleInputChange = (path, value) => {
    const keys = path.split('.');
    setFormData(prev => {
      const newData = { ...prev };
      let current = newData;

      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]]) current[keys[i]] = {};
        current[keys[i]] = { ...current[keys[i]] };
        current = current[keys[i]];
      }

      current[keys[keys.length - 1]] = value;
      return newData;
    });
  };

  const addArrayItem = (arrayName, defaultItem) => {
    setFormData(prev => ({
      ...prev,
      [arrayName]: [...(prev[arrayName] || []), defaultItem]
    }));
  };

  const updateArrayItem = (arrayName, index, field, value) => {
    setFormData(prev => ({
      ...prev,
      [arrayName]: prev[arrayName].map((item, i) =>
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const removeArrayItem = (arrayName, index) => {
    setFormData(prev => ({
      ...prev,
      [arrayName]: prev[arrayName].filter((_, i) => i !== index)
    }));
  };

  // Nested array helpers (for arrays inside objects, e.g. brand_story.stats)
  const addNestedArrayItem = (parentKey, arrayKey, defaultItem) => {
    setFormData(prev => ({
      ...prev,
      [parentKey]: {
        ...(prev[parentKey] || {}),
        [arrayKey]: [...((prev[parentKey] || {})[arrayKey] || []), defaultItem]
      }
    }));
  };

  const updateNestedArrayItem = (parentKey, arrayKey, index, field, value) => {
    setFormData(prev => ({
      ...prev,
      [parentKey]: {
        ...(prev[parentKey] || {}),
        [arrayKey]: ((prev[parentKey] || {})[arrayKey] || []).map((item, i) =>
          i === index ? { ...item, [field]: value } : item
        )
      }
    }));
  };

  const removeNestedArrayItem = (parentKey, arrayKey, index) => {
    setFormData(prev => ({
      ...prev,
      [parentKey]: {
        ...(prev[parentKey] || {}),
        [arrayKey]: ((prev[parentKey] || {})[arrayKey] || []).filter((_, i) => i !== index)
      }
    }));
  };

  // Normalize form data to ensure both field name variants exist for template compatibility
  // Some templates use camelCase (e.g., backgroundImage), others use snake_case (e.g., background_image)
  // Some templates use "name" for categories, others use "title" — we save both so all templates render correctly
  const normalizeFormData = (data) => {
    const normalized = { ...data };

    // Hero: sync backgroundImage <-> background_image, ctaText <-> cta_text, ctaLink <-> cta_link
    if (normalized.hero) {
      normalized.hero = { ...normalized.hero };
      const bgImg = normalized.hero.background_image || normalized.hero.backgroundImage || '';
      normalized.hero.background_image = bgImg;
      normalized.hero.backgroundImage = bgImg;

      const ctaText = normalized.hero.cta_text || normalized.hero.ctaText || '';
      normalized.hero.cta_text = ctaText;
      normalized.hero.ctaText = ctaText;

      const ctaLink = normalized.hero.cta_link || normalized.hero.ctaLink || '';
      normalized.hero.cta_link = ctaLink;
      normalized.hero.ctaLink = ctaLink;
    }

    // Categories: sync name <-> title
    if (Array.isArray(normalized.categories)) {
      normalized.categories = normalized.categories.map(cat => ({
        ...cat,
        name: cat.name || cat.title || '',
        title: cat.title || cat.name || ''
      }));
    }

    // Collections: sync name <-> title
    if (Array.isArray(normalized.collections)) {
      normalized.collections = normalized.collections.map(col => ({
        ...col,
        name: col.name || col.title || '',
        title: col.title || col.name || ''
      }));
    }

    // Fashion trends: sync name <-> text
    if (Array.isArray(normalized.fashion_trends)) {
      normalized.fashion_trends = normalized.fashion_trends.map(trend => ({
        ...trend,
        name: trend.name || trend.text || '',
        text: trend.text || trend.name || ''
      }));
    }

    // Statement: sync backgroundImage <-> background_image
    if (normalized.statement) {
      normalized.statement = { ...normalized.statement };
      const bgImg = normalized.statement.background_image || normalized.statement.backgroundImage || '';
      normalized.statement.background_image = bgImg;
      normalized.statement.backgroundImage = bgImg;
    }

    return normalized;
  };

  const handleFileUpload = (fieldName, file) => {
    if (file) {
      setUploadedFiles(prev => ({
        ...prev,
        [fieldName]: file
      }));

      // Create preview for images
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setPreviewImages(prev => ({
            ...prev,
            [fieldName]: reader.result
          }));
        };
        reader.readAsDataURL(file);
      }
    }
  };

  // Helper function to get YouTube/Vimeo embed URL
  const getVideoEmbedUrl = (url) => {
    if (!url) return null;

    // YouTube
    const youtubeMatch = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
    if (youtubeMatch) {
      return `https://www.youtube.com/embed/${youtubeMatch[1]}`;
    }

    // Vimeo
    const vimeoMatch = url.match(/(?:vimeo\.com\/)(\d+)/);
    if (vimeoMatch) {
      return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
    }

    return null;
  };

  // Video Preview Component
  const VideoPreview = ({ url, onRemove }) => {
    const embedUrl = getVideoEmbedUrl(url);

    if (!url) return null;

    return (
      <div className="mt-4 relative">
        {embedUrl ? (
          // YouTube/Vimeo embed
          <div className="aspect-video rounded-lg overflow-hidden border-2 border-gray-200">
            <iframe
              src={embedUrl}
              title="Video preview"
              className="w-full h-full"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        ) : url.startsWith('http') || url.startsWith('/media') ? (
          // Direct video URL
          <video
            src={url}
            controls
            className="w-full rounded-lg border-2 border-gray-200"
            style={{ maxHeight: '300px' }}
          >
            Your browser does not support the video tag.
          </video>
        ) : null}
        {onRemove && (
          <button
            type="button"
            onClick={onRemove}
            className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>
    );
  };

  // Aspect ratio CSS class mapping
  const getAspectRatioClass = (ratio) => {
    const ratioMap = {
      '1:1': 'aspect-square',           // Square (logo, instagram)
      '16:9': 'aspect-video',           // Video/Banner (hero, thumbnails)
      '4:3': 'aspect-[4/3]',            // Landscape
      '3:4': 'aspect-[3/4]',            // Portrait (categories)
      '4:5': 'aspect-[4/5]',            // Portrait+ (products, gallery)
      '2:3': 'aspect-[2/3]',            // Tall portrait (fashion trends)
      '3:2': 'aspect-[3/2]',            // Wide landscape
      '2:1': 'aspect-[2/1]',            // Wide banner (promo)
      '3:1': 'aspect-[3/1]',            // Extra wide (sliders, banners)
      '21:9': 'aspect-[21/9]',          // Ultra-wide (hero backgrounds)
    };
    return ratioMap[ratio] || 'aspect-video'; // Default to 16:9
  };

  const FileUploadArea = ({ fieldName, label, accept = "image/*", required = false, dimensions = "", description = "", existingUrl = "", aspectRatio = "16:9" }) => {
    const previewSrc = previewImages[fieldName] || existingUrl;
    const isVideo = accept.includes('video');
    const aspectClass = getAspectRatioClass(aspectRatio);

    // Get aspect ratio display text
    const getAspectDisplay = (ratio) => {
      const displays = {
        '1:1': 'Square',
        '16:9': 'Landscape',
        '4:3': 'Standard',
        '3:4': 'Portrait',
        '4:5': 'Portrait',
        '2:3': 'Tall',
        '3:2': 'Wide',
        '2:1': 'Banner',
        '3:1': 'Wide Banner',
        '21:9': 'Ultra Wide'
      };
      return displays[ratio] || ratio;
    };

    return (
      <div className="space-y-2">
        {/* Label with badges */}
        <div className="flex items-center justify-between flex-wrap gap-2">
          <label className="text-sm font-medium text-gray-700">
            {label} {required && <span className="text-red-500">*</span>}
          </label>
          <div className="flex items-center gap-2">
            {dimensions && (
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                {dimensions}
              </span>
            )}
            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-[#D45614]/10 text-[#D45614]">
              {getAspectDisplay(aspectRatio)}
            </span>
          </div>
        </div>
        {description && <p className="text-xs text-gray-500">{description}</p>}

        <div className="relative">
          <input
            type="file"
            accept={accept}
            onChange={(e) => handleFileUpload(fieldName, e.target.files[0])}
            className="hidden"
            id={fieldName}
          />

          {previewSrc && !isVideo ? (
            // Clean image preview
            <div className="relative group">
              <div className={`relative ${aspectClass} w-full max-w-xs overflow-hidden rounded-lg border-2 border-gray-200 bg-gray-50`}>
                <img
                  src={previewSrc}
                  alt="Preview"
                  className="absolute inset-0 w-full h-full object-cover"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
                {/* Hover overlay */}
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                  <label
                    htmlFor={fieldName}
                    className="px-3 py-1.5 bg-white text-gray-800 rounded cursor-pointer hover:bg-gray-100 transition-colors text-sm font-medium"
                  >
                    Change
                  </label>
                  <button
                    type="button"
                    onClick={() => {
                      setUploadedFiles(prev => {
                        const newFiles = { ...prev };
                        delete newFiles[fieldName];
                        return newFiles;
                      });
                      setPreviewImages(prev => {
                        const newPreviews = { ...prev };
                        delete newPreviews[fieldName];
                        return newPreviews;
                      });
                    }}
                    className="px-3 py-1.5 bg-red-500 text-white rounded hover:bg-red-600 transition-colors text-sm font-medium"
                  >
                    Remove
                  </button>
                </div>
                {/* Uploaded indicator */}
                <div className="absolute top-2 right-2 w-6 h-6 bg-[#D45614] rounded-full flex items-center justify-center">
                  <Check className="w-4 h-4 text-white" />
                </div>
              </div>
            </div>
          ) : (
            // Clean upload area
            <label htmlFor={fieldName} className="block cursor-pointer">
              <div className={`${aspectClass} max-w-xs border-2 border-dashed border-gray-300 rounded-lg bg-gray-50 hover:border-[#D45614] hover:bg-[#D45614]/5 transition-colors overflow-hidden`}>
                <div className="absolute inset-0 flex flex-col items-center justify-center p-4">
                  <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-3">
                    {isVideo ? (
                      <PlayCircle className="w-6 h-6 text-gray-400" />
                    ) : (
                      <Upload className="w-6 h-6 text-gray-400" />
                    )}
                  </div>
                  <p className="text-sm font-medium text-gray-600 text-center">
                    {uploadedFiles[fieldName] ? uploadedFiles[fieldName].name : 'Click to upload'}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    {isVideo ? 'MP4, WebM, OGG' : 'PNG, JPG, GIF, SVG'}
                  </p>
                </div>
              </div>
            </label>
          )}
        </div>
      </div>
    );
  };

  // Success Modal Component - Vasstr colors
  const SuccessModal = () => {
    if (!showSuccessModal) return null;
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-black/50" onClick={handleSuccessModalClose}></div>
        <div className="relative bg-white rounded-2xl shadow-xl max-w-sm w-full overflow-hidden border border-gray-200">
          <div className="p-6 text-center">
            <div className="w-16 h-16 bg-[#D45614]/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-[#D45614]" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Saved Successfully!</h3>
            <p className="text-gray-600 text-sm mb-6">Your main page configuration has been saved.</p>
            <button onClick={handleSuccessModalClose} className="w-full bg-[#D45614] hover:bg-[#b8480f] text-white font-medium py-3 px-6 rounded-lg transition-colors flex items-center justify-center gap-2">
              <Check className="w-4 h-4" />
              Continue to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Error Modal Component - Simple design
  const ErrorModal = () => {
    if (!showErrorModal) return null;
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-black/50" onClick={() => setShowErrorModal(false)}></div>
        <div className="relative bg-white rounded-2xl shadow-xl max-w-sm w-full overflow-hidden border border-gray-200">
          <div className="p-6 text-center">
            <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-red-500" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Something went wrong</h3>
            <p className="text-gray-600 text-sm mb-6">{errorMessage}</p>
            <button onClick={() => setShowErrorModal(false)} className="w-full bg-gray-800 hover:bg-gray-900 text-white font-medium py-3 px-6 rounded-lg transition-colors flex items-center justify-center gap-2">
              <X className="w-4 h-4" />
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <Base Content={
        <div className="min-h-screen bg-white flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#D45614] mx-auto mb-4"></div>
            <p className="text-gray-600">Loading main page...</p>
          </div>
        </div>
      } />
    );
  }

  const FormContent = (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-white border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex items-center justify-between h-14">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(`/template-builder/${templateId}`)}
                className="p-2 hover:bg-gray-50 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-gray-500" />
              </button>
              <div>
                <h1 className="text-base font-semibold text-gray-900">Main Page - Template {templateId}</h1>
                {lastSaved && (
                  <p className="text-xs text-gray-400">
                    Saved {lastSaved.toLocaleTimeString()}
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => console.log('Preview:', formData)}
                className="flex items-center gap-2 px-3 py-1.5 text-gray-600 hover:text-[#D45614] transition-colors"
              >
                <Eye className="w-4 h-4" />
                <span className="text-sm">Preview</span>
              </button>
              <button
                onClick={() => handleSave(false)}
                disabled={saving}
                className="flex items-center gap-2 px-4 py-1.5 bg-[#D45614] text-white text-sm font-medium hover:bg-[#c04d12] transition-colors disabled:opacity-50"
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Saving...</span>
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4" />
                    <span>Save</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex gap-8">
          {/* LEFT SIDEBAR - Fixed width */}
          <div className="w-64 flex-shrink-0">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 sticky top-24 p-4">
              {/* Progress Summary */}
              <div className="mb-6">
                <h4 className="text-sm font-semibold text-gray-900 mb-2">Overall Progress</h4>
                <div className="w-full bg-gray-200 rounded-full h-2.5 mb-2">
                  <div
                    className="bg-blue-600 h-2.5 rounded-full transition-all duration-500"
                    style={{ width: `${sections.length > 0 ? Math.round((sections.filter(s => formData[s.id] && Object.keys(formData[s.id]).length > 0).length / sections.length) * 100) : 0}%` }}
                  />
                </div>
                <p className="text-xs text-gray-600">
                  {sections.filter(s => formData[s.id] && Object.keys(formData[s.id]).length > 0).length} of {sections.length} sections
                </p>
              </div>

              {/* Page Sections */}
              <h3 className="text-sm font-semibold text-gray-900 mb-3 px-2">Sections</h3>
              <nav className="space-y-1 max-h-[calc(100vh-350px)] overflow-y-auto pr-1">
                {sections.map((section) => {
                  const SectionIcon = section.icon;
                  const isActive = activeSection === section.id;
                  const hasData = formData[section.id] && Object.keys(formData[section.id]).length > 0;

                  return (
                    <button
                      key={section.id}
                      onClick={() => {
                        setActiveSection(section.id);
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                        isActive
                          ? 'bg-blue-50 text-blue-700 border border-blue-200 shadow-sm'
                          : 'text-gray-700 hover:bg-gray-50 border border-transparent hover:border-gray-200'
                      }`}
                    >
                      <SectionIcon className={`w-5 h-5 flex-shrink-0 ${isActive ? 'text-blue-600' : 'text-gray-400'}`} />
                      <span className="flex-1 text-left truncate text-xs leading-tight">{section.label}</span>
                      {hasData && (
                        <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
                      )}
                      {isActive && !hasData && (
                        <div className="w-2 h-2 rounded-full bg-blue-600 flex-shrink-0" />
                      )}
                    </button>
                  );
                })}
              </nav>
            </div>
          </div>

          {/* RIGHT CONTENT - Flexible width */}
          <div className="flex-1 min-w-0">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 space-y-8">
              {/* Section Header */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                  <h2 className="text-2xl font-bold text-gray-900">
                    {sections.find(s => s.id === activeSection)?.label || 'Section'}
                  </h2>
                  {formData[activeSection] && Object.keys(formData[activeSection]).length > 0 && (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-green-50 text-green-700 rounded-full text-sm font-medium">
                      <Check className="w-4 h-4" />
                      Completed
                    </span>
                  )}
                </div>
              </div>

              {/* Brand Section */}
              {activeSection === 'brand' && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <ShoppingBag className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-3">Brand Identity</h2>
                    <p className="text-gray-600 max-w-2xl mx-auto">
                      Define your brand's visual identity and core information that will represent your business across your website.
                    </p>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    <div className="space-y-6">
                      <FileUploadArea
                        fieldName="brand_logo"
                        label="Brand Logo *"
                        accept="image/*"
                        required={true}
                        dimensions="300x300px"
                        description="Your brand logo will appear across your template"
                        existingUrl={formData.brand?.logo || ''}
                        aspectRatio="1:1"
                      />

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Brand Name *
                        </label>
                        <input
                          type="text"
                          value={formData.brand?.name || ''}
                          onChange={(e) => handleInputChange('brand.name', e.target.value)}
                          placeholder="Enter your brand name"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Tagline
                        </label>
                        <input
                          type="text"
                          value={formData.brand?.tagline || ''}
                          onChange={(e) => handleInputChange('brand.tagline', e.target.value)}
                          placeholder="Your brand tagline"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Brand Description *
                        </label>
                        <textarea
                          value={formData.brand?.description || ''}
                          onChange={(e) => handleInputChange('brand.description', e.target.value)}
                          placeholder="Describe your brand, mission, and what makes you unique..."
                          rows={5}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                          required
                        />
                        <p className="mt-1 text-xs text-gray-500">This will be used in your website's about section</p>
                      </div>
                    </div>

                    <div className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <EnhancedColorPicker
                          label="Primary Color"
                          value={formData.theme?.colors?.primary || '#2C1810'}
                          onChange={(value) => {
                            const newColors = {
                              ...(formData.theme?.colors || {
                                primary: '#2C1810',
                                secondary: '#ffffff',
                                accent: '#C9A882',
                                background: '#ffffff',
                                text: '#111827'
                              }),
                              primary: value
                            };
                            handleInputChange('theme.colors', newColors);
                          }}
                          presetColors={[
                            '#2C1810', '#4A2F23', '#1A0F0A',
                            '#C9A882', '#E5D4B8', '#A68B6A'
                          ]}
                        />
                        <EnhancedColorPicker
                          label="Secondary Color"
                          value={formData.theme?.colors?.secondary || '#ffffff'}
                          onChange={(value) => {
                            const newColors = {
                              ...(formData.theme?.colors || {
                                primary: '#2C1810',
                                secondary: '#ffffff',
                                accent: '#C9A882',
                                background: '#ffffff',
                                text: '#111827'
                              }),
                              secondary: value
                            };
                            handleInputChange('theme.colors', newColors);
                          }}
                          presetColors={[
                            '#ffffff', '#f8f9fa', '#e9ecef',
                            '#dee2e6', '#ced4da', '#adb5bd'
                          ]}
                        />
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <EnhancedColorPicker
                          label="Accent Color"
                          value={formData.theme?.colors?.accent || '#C9A882'}
                          onChange={(value) => {
                            const newColors = {
                              ...(formData.theme?.colors || {
                                primary: '#2C1810',
                                secondary: '#ffffff',
                                accent: '#C9A882',
                                background: '#ffffff',
                                text: '#111827'
                              }),
                              accent: value
                            };
                            handleInputChange('theme.colors', newColors);
                          }}
                          presetColors={[
                            '#C9A882', '#E5D4B8', '#A68B6A',
                            '#D4AF37', '#FFD700', '#B8860B'
                          ]}
                        />
                        <EnhancedColorPicker
                          label="Text Color"
                          value={formData.theme?.colors?.text || '#111827'}
                          onChange={(value) => {
                            const newColors = {
                              ...(formData.theme?.colors || {
                                primary: '#2C1810',
                                secondary: '#ffffff',
                                accent: '#C9A882',
                                background: '#ffffff',
                                text: '#111827'
                              }),
                              text: value
                            };
                            handleInputChange('theme.colors', newColors);
                          }}
                          presetColors={[
                            '#111827', '#1f2937', '#374151',
                            '#4b5563', '#6b7280', '#9ca3af'
                          ]}
                        />
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <EnhancedFontSelector
                          label="Heading Font"
                          value={formData.theme?.typography?.heading || formData.theme?.fonts?.heading || 'sans-serif'}
                          onChange={(value) => handleInputChange('theme.typography.heading', value)}
                          category="all"
                        />
                        <EnhancedFontSelector
                          label="Body Font"
                          value={formData.theme?.typography?.body || formData.theme?.fonts?.body || 'sans-serif'}
                          onChange={(value) => handleInputChange('theme.typography.body', value)}
                          category="all"
                        />
                      </div>

                      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                        <h4 className="font-semibold text-blue-900 mb-2 flex items-center">
                          <Info className="w-4 h-4 mr-2" />
                          Owner Information (Optional)
                        </h4>
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Owner Name
                            </label>
                            <input
                              type="text"
                              value={formData.brand?.owner?.name || ''}
                              onChange={(e) => handleInputChange('brand.owner.name', e.target.value)}
                              placeholder="Your name"
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Owner Bio
                            </label>
                            <textarea
                              value={formData.brand?.owner?.bio || ''}
                              onChange={(e) => handleInputChange('brand.owner.bio', e.target.value)}
                              placeholder="Brief bio about the owner/founder"
                              rows={3}
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Hero Section */}
              {activeSection === 'hero' && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Monitor className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-3">Hero Section</h2>
                    <p className="text-gray-600 max-w-2xl mx-auto">
                      Create an impactful first impression with your hero section that captures visitors' attention immediately.
                    </p>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    <div className="space-y-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Hero Title *</label>
                        <input
                          type="text"
                          value={formData.hero?.title || ''}
                          onChange={(e) => handleInputChange('hero.title', e.target.value)}
                          placeholder="e.g., Welcome to Fashion Excellence"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                          required
                        />
                        <p className="mt-1 text-xs text-gray-500">Main headline that visitors see first</p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Hero Subtitle</label>
                        <input
                          type="text"
                          value={formData.hero?.subtitle || ''}
                          onChange={(e) => handleInputChange('hero.subtitle', e.target.value)}
                          placeholder="e.g., Discover timeless elegance"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                        />
                        <p className="mt-1 text-xs text-gray-500">Supporting text under the main title</p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                        <textarea
                          value={formData.hero?.description || ''}
                          onChange={(e) => handleInputChange('hero.description', e.target.value)}
                          placeholder="Additional descriptive text about your brand or current offers..."
                          rows={3}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Call-to-Action Text</label>
                          <input
                            type="text"
                            value={formData.hero?.cta_text || 'Shop Collection'}
                            onChange={(e) => handleInputChange('hero.cta_text', e.target.value)}
                            placeholder="Shop Now"
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">CTA Link</label>
                          <input
                            type="text"
                            value={formData.hero?.cta_link || '#products'}
                            onChange={(e) => handleInputChange('hero.cta_link', e.target.value)}
                            placeholder="#products"
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="space-y-6">
                      <FileUploadArea
                        fieldName="hero_background"
                        label="Hero Background Image *"
                        accept="image/*"
                        required={true}
                        dimensions="1920x800px recommended"
                        description="High-quality background image for your hero section"
                        existingUrl={formData.hero?.background_image || formData.hero?.backgroundImage || ''}
                        aspectRatio="21:9"
                      />

                      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                        <h4 className="font-semibold text-blue-900 mb-3">Hero Value Tags</h4>
                        <p className="text-xs text-gray-500 mb-3">Short labels shown on the hero section (e.g., "Premium Quality", "Handcrafted")</p>
                        <div className="space-y-2">
                          {formData.hero?.values?.map((val, index) => (
                            <div key={index} className="flex items-center gap-2">
                              <input
                                type="text"
                                value={val.label || ''}
                                onChange={(e) => {
                                  const newValues = [...(formData.hero?.values || [])];
                                  newValues[index] = { ...newValues[index], label: e.target.value };
                                  handleInputChange('hero.values', newValues);
                                }}
                                placeholder={`Label ${index + 1}`}
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary"
                              />
                              <button
                                onClick={() => {
                                  const newValues = (formData.hero?.values || []).filter((_, i) => i !== index);
                                  handleInputChange('hero.values', newValues);
                                }}
                                className="p-2 text-red-600 hover:bg-red-100 rounded-lg"
                              >
                                <Trash2 className="w-3 h-3" />
                              </button>
                            </div>
                          ))}
                          <button
                            onClick={() => {
                              const newValues = [...(formData.hero?.values || []), { label: '' }];
                              handleInputChange('hero.values', newValues);
                            }}
                            className="w-full border border-dashed border-blue-300 rounded-lg p-2 hover:bg-blue-50 transition-all text-sm text-blue-600"
                          >
                            <Plus className="w-4 h-4 inline mr-1" />Add Label
                          </button>
                        </div>
                      </div>

                      <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
                        <h4 className="font-semibold text-purple-900 mb-3">Hero Style Options</h4>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Hero Layout Style</label>
                        <select
                          value={formData.layout?.heroStyle || 'overlay'}
                          onChange={(e) => handleInputChange('layout.heroStyle', e.target.value)}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg"
                        >
                          <option value="minimal">Minimal - Clean and simple</option>
                          <option value="split">Split - Text and image side by side</option>
                          <option value="overlay">Overlay - Text over background image</option>
                          <option value="fullscreen">Fullscreen - Full viewport height</option>
                          <option value="slider">Slider - Multiple hero slides</option>
                          <option value="video">Video - Video background</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Categories Section */}
              {activeSection === 'categories' && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Layers className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-3">Product Categories</h2>
                    <p className="text-gray-600 max-w-2xl mx-auto">
                      Create beautiful product categories to organize your inventory and help customers find what they're looking for.
                    </p>
                  </div>

                  <div className="space-y-6">
                    {formData.categories?.map((category, index) => (
                      <div key={index} className="bg-gradient-to-br from-gray-50 to-gray-100 border border-gray-200 rounded-2xl shadow-sm hover:shadow-md transition-all duration-300">
                        <div className="flex justify-between items-center p-6 pb-4">
                          <div className="flex items-center space-x-3">
                            <span className="w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-bold">
                              {index + 1}
                            </span>
                            <h4 className="text-lg font-semibold text-gray-900">
                              {category.name || `Category ${index + 1}`}
                            </h4>
                          </div>
                          <button
                            onClick={() => removeArrayItem('categories', index)}
                            className="p-2 text-red-600 hover:text-red-800 hover:bg-red-100 rounded-lg transition-colors"
                            title="Delete category"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>

                        <div className="px-6 pb-6">
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Category Name *</label>
                              <input
                                type="text"
                                value={category.name || ''}
                                onChange={(e) => updateArrayItem('categories', index, 'name', e.target.value)}
                                placeholder="e.g., Women's Clothing"
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                                required
                              />
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                              <input
                                type="text"
                                value={category.description || ''}
                                onChange={(e) => updateArrayItem('categories', index, 'description', e.target.value)}
                                placeholder="Brief description"
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                              />
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Item Count</label>
                              <input
                                type="text"
                                value={category.count || ''}
                                onChange={(e) => updateArrayItem('categories', index, 'count', e.target.value)}
                                placeholder="e.g., 150 products"
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                              />
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Gender</label>
                              <select
                                value={category.gender || 'All'}
                                onChange={(e) => updateArrayItem('categories', index, 'gender', e.target.value)}
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                              >
                                <option value="All">All Genders</option>
                                <option value="Men">Men</option>
                                <option value="Women">Women</option>
                                <option value="Kids">Kids</option>
                              </select>
                            </div>

                            <div className="md:col-span-2 lg:col-span-3">
                              <FileUploadArea
                                fieldName={`category_image_${index}`}
                                label="Category Image"
                                accept="image/*"
                                dimensions="400x500px recommended"
                                description="High-quality image representing this category"
                                existingUrl={category.image || ''}
                                aspectRatio="4:5"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}

                    <button
                      onClick={() => addArrayItem('categories', { name: '', title: '', count: '', description: '', gender: 'All', image: '' })}
                      className="w-full border-2 border-dashed border-blue-300 rounded-2xl p-8 hover:border-blue-400 hover:bg-blue-50 transition-all duration-300 group"
                    >
                      <Plus className="w-8 h-8 mx-auto text-blue-400 group-hover:text-blue-600 mb-3" />
                      <span className="text-blue-600 group-hover:text-blue-700 font-medium">
                        Add Category
                      </span>
                    </button>
                  </div>
                </div>
              )}

              {/* Collections Section */}
              {activeSection === 'collections' && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-r from-purple-600 to-pink-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Grid className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-3">Collections</h2>
                    <p className="text-gray-600 max-w-2xl mx-auto">Showcase your featured product collections and highlights</p>
                  </div>

                  <div className="space-y-6">
                    {formData.collections?.map((collection, index) => (
                      <div key={index} className="bg-gradient-to-br from-purple-50 to-pink-50 border border-purple-200 rounded-2xl shadow-sm hover:shadow-md transition-all duration-300">
                        <div className="flex justify-between items-center p-6 pb-4">
                          <div className="flex items-center space-x-3">
                            <span className="w-8 h-8 bg-purple-500 text-white rounded-full flex items-center justify-center text-sm font-bold">
                              {index + 1}
                            </span>
                            <h4 className="text-lg font-semibold text-gray-900">
                              {collection.title || collection.name || `Collection ${index + 1}`}
                            </h4>
                          </div>
                          <button
                            onClick={() => removeArrayItem('collections', index)}
                            className="p-2 text-red-600 hover:text-red-800 hover:bg-red-100 rounded-lg transition-colors"
                            title="Delete collection"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>

                        <div className="px-6 pb-6">
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Collection Name *</label>
                              <input
                                type="text"
                                value={collection.title || collection.name || ''}
                                onChange={(e) => updateArrayItem('collections', index, 'title', e.target.value)}
                                placeholder="e.g., Elite Collection"
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                                required
                              />
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                              <input
                                type="text"
                                value={collection.description || ''}
                                onChange={(e) => updateArrayItem('collections', index, 'description', e.target.value)}
                                placeholder="Brief description"
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                              />
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Item Count</label>
                              <input
                                type="text"
                                value={collection.count || ''}
                                onChange={(e) => updateArrayItem('collections', index, 'count', e.target.value)}
                                placeholder="e.g., 24 pieces"
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                              />
                            </div>

                            <div className="md:col-span-2 lg:col-span-3">
                              <FileUploadArea
                                fieldName={`collection_image_${index}`}
                                label="Collection Image"
                                accept="image/*"
                                dimensions="800x600px recommended"
                                description="Main image for this collection"
                                existingUrl={collection.image || ''}
                                aspectRatio="4:3"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}

                    <button
                      onClick={() => addArrayItem('collections', { title: '', name: '', count: '', description: '', image: '' })}
                      className="w-full border-2 border-dashed border-purple-300 rounded-2xl p-8 hover:border-purple-400 hover:bg-purple-50 transition-all duration-300 group"
                    >
                      <Plus className="w-8 h-8 mx-auto text-purple-400 group-hover:text-purple-600 mb-3" />
                      <span className="text-purple-600 group-hover:text-purple-700 font-medium">
                        Add Collection
                      </span>
                    </button>
                  </div>
                </div>
              )}

              {/* Videos Section */}
              {activeSection === 'videos' && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-r from-purple-600 to-pink-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <PlayCircle className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-3">Video Gallery</h2>
                    <p className="text-gray-600 max-w-2xl mx-auto">
                      Add engaging video content to showcase your products and brand story.
                    </p>
                  </div>

                  <div className="space-y-6">
                    {formData.videos?.map((video, index) => (
                      <div key={index} className="bg-gradient-to-br from-purple-50 to-pink-50 border border-purple-200 rounded-2xl shadow-sm hover:shadow-md transition-all duration-300">
                        <div className="flex justify-between items-center p-6 pb-4">
                          <div className="flex items-center space-x-3">
                            <span className="w-8 h-8 bg-purple-500 text-white rounded-full flex items-center justify-center text-sm font-bold">
                              {index + 1}
                            </span>
                            <h4 className="text-lg font-semibold text-gray-900">
                              {video.title || `Video ${index + 1}`}
                            </h4>
                          </div>
                          <button
                            onClick={() => removeArrayItem('videos', index)}
                            className="p-2 text-red-600 hover:text-red-800 hover:bg-red-100 rounded-lg transition-colors"
                            title="Delete video"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>

                        <div className="px-6 pb-6">
                          <div className="space-y-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Video Title *</label>
                              <input
                                type="text"
                                value={video.title || ''}
                                onChange={(e) => updateArrayItem('videos', index, 'title', e.target.value)}
                                placeholder="e.g., Brand Story"
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                                required
                              />
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                              <textarea
                                value={video.description || ''}
                                onChange={(e) => updateArrayItem('videos', index, 'description', e.target.value)}
                                placeholder="Brief description of the video content..."
                                rows={2}
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                              />
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Video URL (YouTube/Vimeo)</label>
                              <input
                                type="url"
                                value={video.url || ''}
                                onChange={(e) => updateArrayItem('videos', index, 'url', e.target.value)}
                                placeholder="https://youtube.com/watch?v=..."
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                              />
                              <p className="mt-1 text-xs text-gray-500">Or upload a video file below</p>
                              {/* Video Preview for URL */}
                              {video.url && (
                                <VideoPreview
                                  url={video.url}
                                  onRemove={() => updateArrayItem('videos', index, 'url', '')}
                                />
                              )}
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Video Thumbnail</label>
                              <FileUploadArea
                                fieldName={`video_thumbnail_${index}`}
                                label="Upload Thumbnail"
                                accept="image/*"
                                dimensions="640x360px recommended"
                                description="Thumbnail image for the video"
                                existingUrl={video.thumbnail || ''}
                                aspectRatio="16:9"
                              />
                            </div>

                            <FileUploadArea
                              fieldName={`video_file_${index}`}
                              label="Upload Video File (Optional)"
                              accept="video/*"
                              description="Upload video file directly (MP4, WebM, or OGG)"
                              existingUrl={video.file || ''}
                            />
                          </div>
                        </div>
                      </div>
                    ))}

                    <button
                      onClick={() => addArrayItem('videos', { title: '', description: '', url: '', thumbnail: '', file: '' })}
                      className="w-full border-2 border-dashed border-purple-300 rounded-2xl p-8 hover:border-purple-400 hover:bg-purple-50 transition-all duration-300 group"
                    >
                      <Plus className="w-8 h-8 mx-auto text-purple-400 group-hover:text-purple-600 mb-3" />
                      <span className="text-purple-600 group-hover:text-purple-700 font-medium">
                        Add Video
                      </span>
                    </button>
                  </div>
                </div>
              )}


              {/* Business Information Section */}
              {activeSection === 'business' && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-r from-green-600 to-teal-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <FileText className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-3">Business Information</h2>
                    <p className="text-gray-600 max-w-2xl mx-auto">
                      Provide your business contact details and information for customers to reach you.
                    </p>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    <div className="space-y-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Business Name *</label>
                        <input
                          type="text"
                          value={formData.business?.contact?.business_name || ''}
                          onChange={(e) => handleInputChange('business.contact.business_name', e.target.value)}
                          placeholder="Official business name"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Email Address *</label>
                        <input
                          type="email"
                          value={formData.business?.contact?.email || ''}
                          onChange={(e) => handleInputChange('business.contact.email', e.target.value)}
                          placeholder="contact@yourbusiness.com"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number *</label>
                        <input
                          type="tel"
                          value={formData.business?.contact?.phone || ''}
                          onChange={(e) => handleInputChange('business.contact.phone', e.target.value)}
                          placeholder="+1 (555) 123-4567"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                          required
                        />
                      </div>
                    </div>

                    <div className="space-y-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Working Hours</label>
                        <input
                          type="text"
                          value={formData.business?.contact?.working_hours || 'Mon-Sat: 10AM-8PM'}
                          onChange={(e) => handleInputChange('business.contact.working_hours', e.target.value)}
                          placeholder="Mon-Fri 9AM-6PM"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Business Address</label>
                        <textarea
                          value={formData.business?.contact?.address || ''}
                          onChange={(e) => handleInputChange('business.contact.address', e.target.value)}
                          placeholder="123 Business Street, City, State, ZIP"
                          rows={3}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Website URL</label>
                        <input
                          type="url"
                          value={formData.business?.contact?.website || ''}
                          onChange={(e) => handleInputChange('business.contact.website', e.target.value)}
                          placeholder="https://www.yourbusiness.com"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}


              {/* Statement Section */}
              {activeSection === 'statement' && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-r from-rose-600 to-pink-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Quote className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-3">Brand Statement</h2>
                    <p className="text-gray-600 max-w-2xl mx-auto">Create powerful brand messaging that resonates with your audience.</p>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    <div className="space-y-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Main Statement *</label>
                        <textarea
                          value={formData.statement?.title || ''}
                          onChange={(e) => handleInputChange('statement.title', e.target.value)}
                          placeholder="Write your brand's main statement or mission..."
                          rows={4}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Supporting Text</label>
                        <textarea
                          value={formData.statement?.text || ''}
                          onChange={(e) => handleInputChange('statement.text', e.target.value)}
                          placeholder="Additional supporting text for your statement..."
                          rows={3}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                        />
                      </div>
                    </div>
                    <div>
                      <FileUploadArea
                        fieldName="statement_background_image"
                        label="Statement Background Image"
                        accept="image/*"
                        dimensions="1920x800px"
                        description="Background image for the statement section"
                        existingUrl={formData.statement?.background_image || formData.statement?.backgroundImage || ''}
                        aspectRatio="21:9"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Workshop Section */}
              {activeSection === 'workshop' && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-r from-amber-600 to-orange-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Settings className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-3">Workshop Information</h2>
                    <p className="text-gray-600 max-w-2xl mx-auto">Showcase your workshop, studio, or behind-the-scenes content.</p>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    <div className="space-y-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Workshop Title *</label>
                        <input
                          type="text"
                          value={formData.workshop?.title || ''}
                          onChange={(e) => handleInputChange('workshop.title', e.target.value)}
                          placeholder="e.g., Our Artisan Workshop"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Location</label>
                        <input
                          type="text"
                          value={formData.workshop?.location || ''}
                          onChange={(e) => handleInputChange('workshop.location', e.target.value)}
                          placeholder="e.g., Mumbai, India"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Workshop Description</label>
                        <textarea
                          value={formData.workshop?.description || ''}
                          onChange={(e) => handleInputChange('workshop.description', e.target.value)}
                          placeholder="Describe your workshop, processes, and craftsmanship..."
                          rows={4}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                        />
                      </div>
                    </div>
                    <div>
                      <FileUploadArea
                        fieldName="workshop_background"
                        label="Workshop Background Image"
                        accept="image/*"
                        dimensions="1920x800px recommended"
                        description="Background image for the workshop section"
                        existingUrl={formData.workshop?.background_image || ''}
                        aspectRatio="21:9"
                      />
                    </div>
                  </div>

                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-4">Workshop Values</h4>
                    <div className="space-y-4">
                      {formData.workshop?.values?.map((val, index) => (
                        <div key={index} className="bg-gray-50 p-4 rounded-lg space-y-3">
                          <div className="flex justify-between items-center">
                            <span className="w-8 h-8 bg-amber-500 text-white rounded-full flex items-center justify-center text-sm font-bold">{index + 1}</span>
                            <button onClick={() => removeNestedArrayItem('workshop', 'values', index)} className="p-2 text-red-600 hover:bg-red-100 rounded-lg"><Trash2 className="w-4 h-4" /></button>
                          </div>
                          <input type="text" value={val.title || ''} onChange={(e) => updateNestedArrayItem('workshop', 'values', index, 'title', e.target.value)} placeholder="Value title" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" />
                          <textarea value={val.description || ''} onChange={(e) => updateNestedArrayItem('workshop', 'values', index, 'description', e.target.value)} placeholder="Value description..." rows={2} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" />
                        </div>
                      ))}
                      <button onClick={() => addNestedArrayItem('workshop', 'values', { title: '', description: '' })} className="w-full border-2 border-dashed border-amber-300 rounded-xl p-4 hover:border-amber-400 hover:bg-amber-50 transition-all group">
                        <Plus className="w-6 h-6 mx-auto text-amber-400 group-hover:text-amber-600 mb-1" />
                        <span className="text-amber-600 text-sm font-medium">Add Value</span>
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Fashion Trends Section */}
              {activeSection === 'fashion_trends' && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-r from-pink-600 to-rose-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Star className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-3">Fashion Trends</h2>
                    <p className="text-gray-600 max-w-2xl mx-auto">Showcase the latest fashion trends</p>
                  </div>

                  <div className="space-y-6">
                    {formData.fashion_trends?.map((trend, index) => (
                      <div key={index} className="bg-gradient-to-br from-pink-50 to-rose-50 border border-pink-200 rounded-2xl shadow-sm hover:shadow-md transition-all duration-300">
                        <div className="flex justify-between items-center p-6 pb-4">
                          <div className="flex items-center space-x-3">
                            <span className="w-8 h-8 bg-pink-500 text-white rounded-full flex items-center justify-center text-sm font-bold">
                              {index + 1}
                            </span>
                            <h4 className="text-lg font-semibold text-gray-900">
                              {trend.text || `Trend ${index + 1}`}
                            </h4>
                          </div>
                          <button
                            onClick={() => removeArrayItem('fashion_trends', index)}
                            className="p-2 text-red-600 hover:text-red-800 hover:bg-red-100 rounded-lg transition-colors"
                            title="Delete trend"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>

                        <div className="px-6 pb-6">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Trend Name *</label>
                              <input
                                type="text"
                                value={trend.text || ''}
                                onChange={(e) => updateArrayItem('fashion_trends', index, 'text', e.target.value)}
                                placeholder="e.g., Minimalist Chic"
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                                required
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Gender</label>
                              <select
                                value={trend.gender || 'All'}
                                onChange={(e) => updateArrayItem('fashion_trends', index, 'gender', e.target.value)}
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                              >
                                <option value="All">All Genders</option>
                                <option value="Men">Men</option>
                                <option value="Women">Women</option>
                                <option value="Kids">Kids</option>
                              </select>
                            </div>
                          </div>

                          <FileUploadArea
                            fieldName={`fashion_trend_image_${index}`}
                            label="Trend Image"
                            accept="image/*"
                            dimensions="400x600px recommended"
                            description="Image showcasing this fashion trend"
                            existingUrl={trend.image || ''}
                            aspectRatio="2:3"
                          />
                        </div>
                      </div>
                    ))}

                    <button
                      onClick={() => addArrayItem('fashion_trends', { name: '', text: '', gender: 'All', image: '' })}
                      className="w-full border-2 border-dashed border-pink-300 rounded-2xl p-8 hover:border-pink-400 hover:bg-pink-50 transition-all duration-300 group"
                    >
                      <Plus className="w-8 h-8 mx-auto text-pink-400 group-hover:text-pink-600 mb-3" />
                      <span className="text-pink-600 group-hover:text-pink-700 font-medium">
                        Add Trend
                      </span>
                    </button>
                  </div>
                </div>
              )}

              {/* Gallery Section */}
              {activeSection === 'gallery' && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-r from-teal-600 to-cyan-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <ImageIcon className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-3">Gallery</h2>
                    <p className="text-gray-600 max-w-2xl mx-auto">Create a beautiful image gallery</p>
                  </div>

                  <div className="space-y-6">
                    {formData.gallery?.map((item, index) => (
                      <div key={index} className="bg-gradient-to-br from-teal-50 to-cyan-50 border border-teal-200 rounded-2xl shadow-sm hover:shadow-md transition-all duration-300">
                        <div className="flex justify-between items-center p-6 pb-4">
                          <div className="flex items-center space-x-3">
                            <span className="w-8 h-8 bg-teal-500 text-white rounded-full flex items-center justify-center text-sm font-bold">
                              {index + 1}
                            </span>
                            <h4 className="text-lg font-semibold text-gray-900">
                              {item.title || `Gallery Image ${index + 1}`}
                            </h4>
                          </div>
                          <button
                            onClick={() => removeArrayItem('gallery', index)}
                            className="p-2 text-red-600 hover:text-red-800 hover:bg-red-100 rounded-lg transition-colors"
                            title="Delete image"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>

                        <div className="px-6 pb-6">
                          <div className="mb-6">
                            <label className="block text-sm font-medium text-gray-700 mb-2">Image Title *</label>
                            <input
                              type="text"
                              value={item.title || ''}
                              onChange={(e) => updateArrayItem('gallery', index, 'title', e.target.value)}
                              placeholder="e.g., Portrait Series"
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                              required
                            />
                          </div>

                          <FileUploadArea
                            fieldName={`gallery_image_${index}`}
                            label="Gallery Image"
                            accept="image/*"
                            dimensions="600x750px recommended"
                            description={`Gallery image ${index + 1}`}
                            existingUrl={item.image || ''}
                            aspectRatio="4:5"
                          />
                        </div>
                      </div>
                    ))}

                    <button
                      onClick={() => addArrayItem('gallery', { title: '', image: '' })}
                      className="w-full border-2 border-dashed border-teal-300 rounded-2xl p-8 hover:border-teal-400 hover:bg-teal-50 transition-all duration-300 group"
                    >
                      <Plus className="w-8 h-8 mx-auto text-teal-400 group-hover:text-teal-600 mb-3" />
                      <span className="text-teal-600 group-hover:text-teal-700 font-medium">
                        Add Image
                      </span>
                    </button>
                  </div>
                </div>
              )}

              {/* Instagram Section */}
              {activeSection === 'instagram' && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-r from-purple-600 to-pink-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <ImageIcon className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-3">Instagram Feed</h2>
                    <p className="text-gray-600 max-w-2xl mx-auto">Showcase your Instagram posts</p>
                  </div>

                  <div className="space-y-6">
                    {formData.instagram?.map((post, index) => (
                      <div key={index} className="bg-gradient-to-br from-purple-50 to-pink-50 border border-purple-200 rounded-2xl shadow-sm hover:shadow-md transition-all duration-300">
                        <div className="flex justify-between items-center p-6 pb-4">
                          <div className="flex items-center space-x-3">
                            <span className="w-8 h-8 bg-purple-500 text-white rounded-full flex items-center justify-center text-sm font-bold">
                              {index + 1}
                            </span>
                            <h4 className="text-lg font-semibold text-gray-900">
                              {`Post ${index + 1}`}
                            </h4>
                          </div>
                          <button
                            onClick={() => removeArrayItem('instagram', index)}
                            className="p-2 text-red-600 hover:text-red-800 hover:bg-red-100 rounded-lg transition-colors"
                            title="Delete post"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>

                        <div className="px-6 pb-6">
                          <FileUploadArea
                            fieldName={`instagram_image_${index}`}
                            label="Instagram Image"
                            accept="image/*"
                            dimensions="1080x1080px recommended"
                            description="Square image for Instagram feed"
                            existingUrl={post.image || ''}
                            aspectRatio="1:1"
                          />
                        </div>
                      </div>
                    ))}

                    <button
                      onClick={() => addArrayItem('instagram', { image: '' })}
                      className="w-full border-2 border-dashed border-purple-300 rounded-2xl p-8 hover:border-purple-400 hover:bg-purple-50 transition-all duration-300 group"
                    >
                      <Plus className="w-8 h-8 mx-auto text-purple-400 group-hover:text-purple-600 mb-3" />
                      <span className="text-purple-600 group-hover:text-purple-700 font-medium">
                        Add Post
                      </span>
                    </button>
                  </div>
                </div>
              )}

              {/* About Section */}
              {activeSection === 'about' && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-cyan-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Info className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-3">About / Vision</h2>
                    <p className="text-gray-600 max-w-2xl mx-auto">Share your brand's vision and story with your audience.</p>
                  </div>
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Title *</label>
                      <input
                        type="text"
                        value={formData.about?.title || ''}
                        onChange={(e) => handleInputChange('about.title', e.target.value)}
                        placeholder="e.g., Our Vision"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Description *</label>
                      <textarea
                        value={formData.about?.description || ''}
                        onChange={(e) => handleInputChange('about.description', e.target.value)}
                        placeholder="Describe your brand's vision and mission..."
                        rows={5}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                        required
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Lookbook Section */}
              {activeSection === 'lookbook' && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-r from-amber-600 to-orange-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <BookOpen className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-3">Lookbook</h2>
                    <p className="text-gray-600 max-w-2xl mx-auto">Create a visual lookbook to showcase your collections.</p>
                  </div>
                  <div className="space-y-6">
                    {formData.lookbook?.map((item, index) => (
                      <div key={index} className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-2xl shadow-sm hover:shadow-md transition-all duration-300">
                        <div className="flex justify-between items-center p-6 pb-4">
                          <div className="flex items-center space-x-3">
                            <span className="w-8 h-8 bg-amber-500 text-white rounded-full flex items-center justify-center text-sm font-bold">{index + 1}</span>
                            <h4 className="text-lg font-semibold text-gray-900">{item.title || `Lookbook ${index + 1}`}</h4>
                          </div>
                          <button onClick={() => removeArrayItem('lookbook', index)} className="p-2 text-red-600 hover:text-red-800 hover:bg-red-100 rounded-lg transition-colors" title="Delete item">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                        <div className="px-6 pb-6">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-4">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Title *</label>
                                <input type="text" value={item.title || ''} onChange={(e) => updateArrayItem('lookbook', index, 'title', e.target.value)} placeholder="e.g., Summer Collection" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" required />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Subtitle</label>
                                <input type="text" value={item.subtitle || ''} onChange={(e) => updateArrayItem('lookbook', index, 'subtitle', e.target.value)} placeholder="e.g., Inspired by nature" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" />
                              </div>
                            </div>
                            <FileUploadArea fieldName={`lookbook_image_${index}`} label="Lookbook Image" accept="image/*" dimensions="800x1000px recommended" description="High-quality lookbook image" existingUrl={item.image || ''} aspectRatio="4:5" />
                          </div>
                        </div>
                      </div>
                    ))}
                    <button onClick={() => addArrayItem('lookbook', { title: '', subtitle: '', image: '' })} className="w-full border-2 border-dashed border-amber-300 rounded-2xl p-8 hover:border-amber-400 hover:bg-amber-50 transition-all duration-300 group">
                      <Plus className="w-8 h-8 mx-auto text-amber-400 group-hover:text-amber-600 mb-3" />
                      <span className="text-amber-600 group-hover:text-amber-700 font-medium">Add Lookbook Item</span>
                    </button>
                  </div>
                </div>
              )}

              {/* Featured Products Section */}
              {activeSection === 'featured' && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-r from-yellow-600 to-amber-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Star className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-3">Featured Products</h2>
                    <p className="text-gray-600 max-w-2xl mx-auto">Highlight your best products to feature on the homepage.</p>
                  </div>
                  <div className="space-y-6">
                    {formData.featured?.map((item, index) => (
                      <div key={index} className="bg-gradient-to-br from-yellow-50 to-amber-50 border border-yellow-200 rounded-2xl shadow-sm hover:shadow-md transition-all duration-300">
                        <div className="flex justify-between items-center p-6 pb-4">
                          <div className="flex items-center space-x-3">
                            <span className="w-8 h-8 bg-yellow-500 text-white rounded-full flex items-center justify-center text-sm font-bold">{index + 1}</span>
                            <h4 className="text-lg font-semibold text-gray-900">{item.title || `Product ${index + 1}`}</h4>
                          </div>
                          <button onClick={() => removeArrayItem('featured', index)} className="p-2 text-red-600 hover:text-red-800 hover:bg-red-100 rounded-lg transition-colors" title="Delete item">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                        <div className="px-6 pb-6">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-4">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Product Title *</label>
                                <input type="text" value={item.title || ''} onChange={(e) => updateArrayItem('featured', index, 'title', e.target.value)} placeholder="e.g., Silk Evening Gown" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" required />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Price</label>
                                <input type="text" value={item.price || ''} onChange={(e) => updateArrayItem('featured', index, 'price', e.target.value)} placeholder="e.g., ₹2,999" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" />
                              </div>
                            </div>
                            <FileUploadArea fieldName={`featured_image_${index}`} label="Product Image" accept="image/*" dimensions="600x800px recommended" description="Product image" existingUrl={item.image || ''} aspectRatio="3:4" />
                          </div>
                        </div>
                      </div>
                    ))}
                    <button onClick={() => addArrayItem('featured', { title: '', image: '', price: '' })} className="w-full border-2 border-dashed border-yellow-300 rounded-2xl p-8 hover:border-yellow-400 hover:bg-yellow-50 transition-all duration-300 group">
                      <Plus className="w-8 h-8 mx-auto text-yellow-400 group-hover:text-yellow-600 mb-3" />
                      <span className="text-yellow-600 group-hover:text-yellow-700 font-medium">Add Featured Product</span>
                    </button>
                  </div>
                </div>
              )}

              {/* Brand Story Section */}
              {activeSection === 'brand_story' && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-r from-emerald-600 to-teal-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <BookOpen className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-3">Brand Story</h2>
                    <p className="text-gray-600 max-w-2xl mx-auto">Tell the story behind your brand with compelling stats.</p>
                  </div>
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Title *</label>
                      <input type="text" value={formData.brand_story?.title || ''} onChange={(e) => handleInputChange('brand_story.title', e.target.value)} placeholder="e.g., Our Story" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" required />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Description *</label>
                      <textarea value={formData.brand_story?.description || ''} onChange={(e) => handleInputChange('brand_story.description', e.target.value)} placeholder="Tell your brand story..." rows={5} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" required />
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-gray-900 mb-4">Stats</h4>
                      <div className="space-y-4">
                        {formData.brand_story?.stats?.map((stat, index) => (
                          <div key={index} className="flex items-center gap-4 bg-gray-50 p-4 rounded-lg">
                            <span className="w-8 h-8 bg-emerald-500 text-white rounded-full flex items-center justify-center text-sm font-bold">{index + 1}</span>
                            <input type="text" value={stat.value || ''} onChange={(e) => updateNestedArrayItem('brand_story', 'stats', index, 'value', e.target.value)} placeholder="e.g., 10+" className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" />
                            <input type="text" value={stat.label || ''} onChange={(e) => updateNestedArrayItem('brand_story', 'stats', index, 'label', e.target.value)} placeholder="e.g., Years of Experience" className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" />
                            <button onClick={() => removeNestedArrayItem('brand_story', 'stats', index)} className="p-2 text-red-600 hover:bg-red-100 rounded-lg"><Trash2 className="w-4 h-4" /></button>
                          </div>
                        ))}
                        <button onClick={() => addNestedArrayItem('brand_story', 'stats', { value: '', label: '' })} className="w-full border-2 border-dashed border-emerald-300 rounded-xl p-4 hover:border-emerald-400 hover:bg-emerald-50 transition-all group">
                          <Plus className="w-6 h-6 mx-auto text-emerald-400 group-hover:text-emerald-600 mb-1" />
                          <span className="text-emerald-600 text-sm font-medium">Add Stat</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Video Section (single video) */}
              {activeSection === 'video' && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-r from-purple-600 to-pink-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <PlayCircle className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-3">Video</h2>
                    <p className="text-gray-600 max-w-2xl mx-auto">Add a featured video to showcase your brand.</p>
                  </div>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    <div className="space-y-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Video URL (YouTube/Vimeo)</label>
                        <input type="url" value={formData.video?.url || ''} onChange={(e) => handleInputChange('video.url', e.target.value)} placeholder="https://youtube.com/watch?v=..." className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" />
                        <p className="mt-1 text-xs text-gray-500">Paste a YouTube or Vimeo link</p>
                        {formData.video?.url && (
                          <VideoPreview url={formData.video.url} onRemove={() => handleInputChange('video.url', '')} />
                        )}
                      </div>
                    </div>
                    <div>
                      <FileUploadArea fieldName="video_thumbnail" label="Video Thumbnail" accept="image/*" dimensions="1280x720px recommended" description="Thumbnail image for the video" existingUrl={formData.video?.thumbnail || ''} aspectRatio="16:9" />
                    </div>
                  </div>
                </div>
              )}

              {/* Luxury Statement Section */}
              {activeSection === 'luxury_statement' && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-r from-amber-600 to-yellow-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Quote className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-3">Luxury Statement</h2>
                    <p className="text-gray-600 max-w-2xl mx-auto">Define your luxury brand identity with a powerful statement.</p>
                  </div>
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Title *</label>
                      <input type="text" value={formData.luxury_statement?.title || ''} onChange={(e) => handleInputChange('luxury_statement.title', e.target.value)} placeholder="e.g., Timeless Luxury" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" required />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Description *</label>
                      <textarea value={formData.luxury_statement?.description || ''} onChange={(e) => handleInputChange('luxury_statement.description', e.target.value)} placeholder="Describe your luxury brand philosophy..." rows={5} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" required />
                    </div>
                  </div>
                </div>
              )}

              {/* Prestige Services Section */}
              {activeSection === 'prestige_services' && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Star className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-3">Prestige Services</h2>
                    <p className="text-gray-600 max-w-2xl mx-auto">Highlight the premium services you offer.</p>
                  </div>
                  <div className="space-y-6">
                    {formData.prestige_services?.map((service, index) => (
                      <div key={index} className="bg-gradient-to-br from-indigo-50 to-purple-50 border border-indigo-200 rounded-2xl shadow-sm hover:shadow-md transition-all duration-300">
                        <div className="flex justify-between items-center p-6 pb-4">
                          <div className="flex items-center space-x-3">
                            <span className="w-8 h-8 bg-indigo-500 text-white rounded-full flex items-center justify-center text-sm font-bold">{index + 1}</span>
                            <h4 className="text-lg font-semibold text-gray-900">{service.title || `Service ${index + 1}`}</h4>
                          </div>
                          <button onClick={() => removeArrayItem('prestige_services', index)} className="p-2 text-red-600 hover:text-red-800 hover:bg-red-100 rounded-lg transition-colors" title="Delete service">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                        <div className="px-6 pb-6 space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Service Title *</label>
                            <input type="text" value={service.title || ''} onChange={(e) => updateArrayItem('prestige_services', index, 'title', e.target.value)} placeholder="e.g., Personal Styling" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" required />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                            <textarea value={service.description || ''} onChange={(e) => updateArrayItem('prestige_services', index, 'description', e.target.value)} placeholder="Describe this service..." rows={3} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" />
                          </div>
                        </div>
                      </div>
                    ))}
                    <button onClick={() => addArrayItem('prestige_services', { title: '', description: '' })} className="w-full border-2 border-dashed border-indigo-300 rounded-2xl p-8 hover:border-indigo-400 hover:bg-indigo-50 transition-all duration-300 group">
                      <Plus className="w-8 h-8 mx-auto text-indigo-400 group-hover:text-indigo-600 mb-3" />
                      <span className="text-indigo-600 group-hover:text-indigo-700 font-medium">Add Service</span>
                    </button>
                  </div>
                </div>
              )}

              {/* Craftsmanship Section */}
              {activeSection === 'craftsmanship' && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-r from-amber-600 to-orange-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Settings className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-3">Craftsmanship</h2>
                    <p className="text-gray-600 max-w-2xl mx-auto">Showcase the art and skill behind your products.</p>
                  </div>
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Title *</label>
                      <input type="text" value={formData.craftsmanship?.title || ''} onChange={(e) => handleInputChange('craftsmanship.title', e.target.value)} placeholder="e.g., The Art of Craftsmanship" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" required />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Description *</label>
                      <textarea value={formData.craftsmanship?.description || ''} onChange={(e) => handleInputChange('craftsmanship.description', e.target.value)} placeholder="Describe your craftsmanship process..." rows={5} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" required />
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-gray-900 mb-4">Stats</h4>
                      <div className="space-y-4">
                        {formData.craftsmanship?.stats?.map((stat, index) => (
                          <div key={index} className="flex items-center gap-4 bg-gray-50 p-4 rounded-lg">
                            <span className="w-8 h-8 bg-amber-500 text-white rounded-full flex items-center justify-center text-sm font-bold">{index + 1}</span>
                            <input type="text" value={stat.value || ''} onChange={(e) => updateNestedArrayItem('craftsmanship', 'stats', index, 'value', e.target.value)} placeholder="e.g., 50+" className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" />
                            <input type="text" value={stat.label || ''} onChange={(e) => updateNestedArrayItem('craftsmanship', 'stats', index, 'label', e.target.value)} placeholder="e.g., Artisans" className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" />
                            <button onClick={() => removeNestedArrayItem('craftsmanship', 'stats', index)} className="p-2 text-red-600 hover:bg-red-100 rounded-lg"><Trash2 className="w-4 h-4" /></button>
                          </div>
                        ))}
                        <button onClick={() => addNestedArrayItem('craftsmanship', 'stats', { value: '', label: '' })} className="w-full border-2 border-dashed border-amber-300 rounded-xl p-4 hover:border-amber-400 hover:bg-amber-50 transition-all group">
                          <Plus className="w-6 h-6 mx-auto text-amber-400 group-hover:text-amber-600 mb-1" />
                          <span className="text-amber-600 text-sm font-medium">Add Stat</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Human Connection Section */}
              {activeSection === 'human_connection' && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-r from-rose-600 to-pink-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Heart className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-3">Human Connection</h2>
                    <p className="text-gray-600 max-w-2xl mx-auto">Share a personal touch that connects with your audience.</p>
                  </div>
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Title *</label>
                      <input type="text" value={formData.human_connection?.title || ''} onChange={(e) => handleInputChange('human_connection.title', e.target.value)} placeholder="e.g., Made with Love" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" required />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Quote *</label>
                      <textarea value={formData.human_connection?.quote || ''} onChange={(e) => handleInputChange('human_connection.quote', e.target.value)} placeholder="A meaningful quote that represents your brand..." rows={4} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" required />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Author</label>
                      <input type="text" value={formData.human_connection?.author || ''} onChange={(e) => handleInputChange('human_connection.author', e.target.value)} placeholder="e.g., Founder, Brand Name" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" />
                    </div>
                  </div>
                </div>
              )}

              {/* Brand Statement Section */}
              {activeSection === 'brand_statement' && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-r from-gray-700 to-gray-900 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Quote className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-3">Brand Statement</h2>
                    <p className="text-gray-600 max-w-2xl mx-auto">Define your brand with a powerful statement and core values.</p>
                  </div>
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Title *</label>
                      <input type="text" value={formData.brand_statement?.title || ''} onChange={(e) => handleInputChange('brand_statement.title', e.target.value)} placeholder="e.g., What We Stand For" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" required />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Description *</label>
                      <textarea value={formData.brand_statement?.description || ''} onChange={(e) => handleInputChange('brand_statement.description', e.target.value)} placeholder="Your brand statement..." rows={5} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" required />
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-gray-900 mb-4">Values</h4>
                      <div className="space-y-4">
                        {formData.brand_statement?.values?.map((val, index) => (
                          <div key={index} className="bg-gray-50 p-4 rounded-lg space-y-3">
                            <div className="flex justify-between items-center">
                              <span className="w-8 h-8 bg-gray-700 text-white rounded-full flex items-center justify-center text-sm font-bold">{index + 1}</span>
                              <button onClick={() => removeNestedArrayItem('brand_statement', 'values', index)} className="p-2 text-red-600 hover:bg-red-100 rounded-lg"><Trash2 className="w-4 h-4" /></button>
                            </div>
                            <input type="text" value={val.title || ''} onChange={(e) => updateNestedArrayItem('brand_statement', 'values', index, 'title', e.target.value)} placeholder="Value title" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" />
                            <textarea value={val.description || ''} onChange={(e) => updateNestedArrayItem('brand_statement', 'values', index, 'description', e.target.value)} placeholder="Value description..." rows={2} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" />
                          </div>
                        ))}
                        <button onClick={() => addNestedArrayItem('brand_statement', 'values', { title: '', description: '' })} className="w-full border-2 border-dashed border-gray-300 rounded-xl p-4 hover:border-gray-400 hover:bg-gray-50 transition-all group">
                          <Plus className="w-6 h-6 mx-auto text-gray-400 group-hover:text-gray-600 mb-1" />
                          <span className="text-gray-600 text-sm font-medium">Add Value</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Featured Stories Section */}
              {activeSection === 'featured_stories' && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <FileText className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-3">Featured Stories</h2>
                    <p className="text-gray-600 max-w-2xl mx-auto">Share stories that highlight your brand and products.</p>
                  </div>
                  <div className="space-y-6">
                    {formData.featured_stories?.map((story, index) => (
                      <div key={index} className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-2xl shadow-sm hover:shadow-md transition-all duration-300">
                        <div className="flex justify-between items-center p-6 pb-4">
                          <div className="flex items-center space-x-3">
                            <span className="w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-bold">{index + 1}</span>
                            <h4 className="text-lg font-semibold text-gray-900">{story.title || `Story ${index + 1}`}</h4>
                          </div>
                          <button onClick={() => removeArrayItem('featured_stories', index)} className="p-2 text-red-600 hover:text-red-800 hover:bg-red-100 rounded-lg transition-colors" title="Delete story">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                        <div className="px-6 pb-6">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-4">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Title *</label>
                                <input type="text" value={story.title || ''} onChange={(e) => updateArrayItem('featured_stories', index, 'title', e.target.value)} placeholder="Story title" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" required />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Excerpt</label>
                                <textarea value={story.excerpt || ''} onChange={(e) => updateArrayItem('featured_stories', index, 'excerpt', e.target.value)} placeholder="Brief excerpt..." rows={3} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                                <input type="text" value={story.category || ''} onChange={(e) => updateArrayItem('featured_stories', index, 'category', e.target.value)} placeholder="e.g., Fashion, Lifestyle" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" />
                              </div>
                            </div>
                            <FileUploadArea fieldName={`featured_story_image_${index}`} label="Story Image" accept="image/*" dimensions="800x600px recommended" description="Story cover image" existingUrl={story.image || ''} aspectRatio="4:3" />
                          </div>
                        </div>
                      </div>
                    ))}
                    <button onClick={() => addArrayItem('featured_stories', { title: '', excerpt: '', category: '', image: '' })} className="w-full border-2 border-dashed border-blue-300 rounded-2xl p-8 hover:border-blue-400 hover:bg-blue-50 transition-all duration-300 group">
                      <Plus className="w-8 h-8 mx-auto text-blue-400 group-hover:text-blue-600 mb-3" />
                      <span className="text-blue-600 group-hover:text-blue-700 font-medium">Add Story</span>
                    </button>
                  </div>
                </div>
              )}

              {/* Quote Section */}
              {activeSection === 'quote' && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-r from-rose-600 to-pink-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Quote className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-3">Quote</h2>
                    <p className="text-gray-600 max-w-2xl mx-auto">Add an inspirational quote to your page.</p>
                  </div>
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Quote Text *</label>
                      <textarea value={formData.quote?.text || ''} onChange={(e) => handleInputChange('quote.text', e.target.value)} placeholder="Enter the quote text..." rows={4} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" required />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Author</label>
                      <input type="text" value={formData.quote?.author || ''} onChange={(e) => handleInputChange('quote.author', e.target.value)} placeholder="e.g., Jane Doe, Founder" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" />
                    </div>
                  </div>
                </div>
              )}

              {/* Portfolio Section */}
              {activeSection === 'portfolio' && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-r from-teal-600 to-cyan-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <ImageIcon className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-3">Portfolio</h2>
                    <p className="text-gray-600 max-w-2xl mx-auto">Showcase your best work in a portfolio gallery.</p>
                  </div>
                  <div className="space-y-6">
                    {formData.portfolio?.map((item, index) => (
                      <div key={index} className="bg-gradient-to-br from-teal-50 to-cyan-50 border border-teal-200 rounded-2xl shadow-sm hover:shadow-md transition-all duration-300">
                        <div className="flex justify-between items-center p-6 pb-4">
                          <div className="flex items-center space-x-3">
                            <span className="w-8 h-8 bg-teal-500 text-white rounded-full flex items-center justify-center text-sm font-bold">{index + 1}</span>
                            <h4 className="text-lg font-semibold text-gray-900">{item.title || `Portfolio ${index + 1}`}</h4>
                          </div>
                          <button onClick={() => removeArrayItem('portfolio', index)} className="p-2 text-red-600 hover:text-red-800 hover:bg-red-100 rounded-lg transition-colors" title="Delete item">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                        <div className="px-6 pb-6">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Title *</label>
                              <input type="text" value={item.title || ''} onChange={(e) => updateArrayItem('portfolio', index, 'title', e.target.value)} placeholder="e.g., Evening Collection" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" required />
                            </div>
                            <FileUploadArea fieldName={`portfolio_image_${index}`} label="Portfolio Image" accept="image/*" dimensions="800x600px recommended" description="Portfolio piece image" existingUrl={item.image || ''} aspectRatio="4:3" />
                          </div>
                        </div>
                      </div>
                    ))}
                    <button onClick={() => addArrayItem('portfolio', { title: '', image: '' })} className="w-full border-2 border-dashed border-teal-300 rounded-2xl p-8 hover:border-teal-400 hover:bg-teal-50 transition-all duration-300 group">
                      <Plus className="w-8 h-8 mx-auto text-teal-400 group-hover:text-teal-600 mb-3" />
                      <span className="text-teal-600 group-hover:text-teal-700 font-medium">Add Portfolio Item</span>
                    </button>
                  </div>
                </div>
              )}

              {/* Artist Statement Section */}
              {activeSection === 'artist_statement' && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-r from-violet-600 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Quote className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-3">Artist Statement</h2>
                    <p className="text-gray-600 max-w-2xl mx-auto">Express your artistic vision and philosophy.</p>
                  </div>
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Title *</label>
                      <input type="text" value={formData.artist_statement?.title || ''} onChange={(e) => handleInputChange('artist_statement.title', e.target.value)} placeholder="e.g., My Artistic Vision" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" required />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Statement *</label>
                      <textarea value={formData.artist_statement?.text || ''} onChange={(e) => handleInputChange('artist_statement.text', e.target.value)} placeholder="Share your artistic statement and philosophy..." rows={6} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" required />
                    </div>
                  </div>
                </div>
              )}

              {/* Navigation Buttons */}
              {sections.length > 1 && (
                <div className="mt-8 pt-6 border-t border-gray-200 flex justify-between items-center">
                  <button
                    onClick={() => {
                      const currentIndex = sections.findIndex(s => s.id === activeSection);
                      if (currentIndex > 0) {
                        setActiveSection(sections[currentIndex - 1].id);
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }
                    }}
                    disabled={sections.findIndex(s => s.id === activeSection) === 0}
                    className="flex items-center space-x-2 px-6 py-3 bg-gray-100 text-gray-700 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ArrowLeft className="w-5 h-5" />
                    <span className="font-medium">Previous Section</span>
                  </button>

                  <div className="text-sm text-gray-600 font-medium">
                    Section {sections.findIndex(s => s.id === activeSection) + 1} of {sections.length}
                  </div>

                  <button
                    onClick={() => {
                      const currentIndex = sections.findIndex(s => s.id === activeSection);
                      if (currentIndex < sections.length - 1) {
                        setActiveSection(sections[currentIndex + 1].id);
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }
                    }}
                    disabled={sections.findIndex(s => s.id === activeSection) === sections.length - 1}
                    className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:from-blue-700 hover:to-indigo-700 rounded-lg transition-all shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span className="font-medium">
                      {sections.findIndex(s => s.id === activeSection) === sections.length - 1 ? 'Last Section' : 'Next Section'}
                    </span>
                    {sections.findIndex(s => s.id === activeSection) < sections.length - 1 ? (
                      <ArrowRight className="w-5 h-5" />
                    ) : (
                      <Check className="w-5 h-5" />
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      <SuccessModal />
      <ErrorModal />
    </div>
  );

  return <Base Content={FormContent} />;
};

export default MainPageForm;
